import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ShieldCheck,
  ShieldOff,
  Loader2,
  KeyRound,
  Copy,
  Check,
  LogOut,
  RefreshCw,
  Lock,
  Download,
  Smartphone,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ProfilePage() {
  const qc = useQueryClient();
  const { user, logout, refresh } = useAuth();

  const { data: status, isLoading } = useQuery({
    queryKey: ["2fa-status"],
    queryFn: () => api.get("/auth/2fa/status").then((r) => r.data),
  });

  const [setupData, setSetupData] = useState(null);
  const [setupCode, setSetupCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const [pwPrompt, setPwPrompt] = useState(null); // "disable" | "regen"
  const [promptPassword, setPromptPassword] = useState("");

  const [cpCurrent, setCpCurrent] = useState("");
  const [cpNew, setCpNew] = useState("");
  const [cpConfirm, setCpConfirm] = useState("");

  const inputClass =
    "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20";
  const labelClass = "mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500";

  const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetupData(data);
      setSetupCode("");
    } catch (e) {
      err(e);
    } finally {
      setBusy(false);
    }
  };

  const confirmEnable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enable", { code: setupCode.trim() });
      setBackupCodes(data.backup_codes);
      setSetupData(null);
      setSetupCode("");
      qc.invalidateQueries({ queryKey: ["2fa-status"] });
      toast.success("Двуфакторната автентикация е включена.");
    } catch (e) {
      err(e);
    } finally {
      setBusy(false);
    }
  };

  const submitPwPrompt = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (pwPrompt === "disable") {
        await api.post("/auth/2fa/disable", { password: promptPassword });
        setBackupCodes(null);
        toast.success("Двуфакторната автентикация е изключена.");
      } else if (pwPrompt === "regen") {
        const { data } = await api.post("/auth/2fa/backup-codes", { password: promptPassword });
        setBackupCodes(data.backup_codes);
        toast.success("Генерирани са нови резервни кодове.");
      }
      qc.invalidateQueries({ queryKey: ["2fa-status"] });
      setPwPrompt(null);
      setPromptPassword("");
    } catch (e) {
      err(e);
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (cpNew !== cpConfirm) {
      toast.error("Новите пароли не съвпадат.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: cpCurrent,
        new_password: cpNew,
      });
      if (data.access_token) localStorage.setItem("pd_token", data.access_token);
      setCpCurrent("");
      setCpNew("");
      setCpConfirm("");
      toast.success("Паролата е сменена. Другите устройства са излезли.");
      if (refresh) refresh();
    } catch (e) {
      err(e);
    } finally {
      setBusy(false);
    }
  };

  const logoutAll = async () => {
    if (!window.confirm("Изход от всички устройства? Ще трябва да влезете отново.")) return;
    try {
      await api.post("/auth/logout-all");
    } catch (e) {
      /* ignore */
    }
    await logout();
  };

  const copyCodes = () => {
    navigator.clipboard.writeText((backupCodes || []).join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const downloadCodes = () => {
    const blob = new Blob(
      [
        "Резервни кодове — Подуяне Днес\n" +
          "Всеки код може да се използва еднократно.\n\n" +
          (backupCodes || []).join("\n"),
      ],
      { type: "text/plain" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "poduyane-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  const enabled = !!status?.enabled;

  return (
    <div className="mt-6 max-w-2xl space-y-6" data-testid="profile-page">
      {/* Account */}
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="font-display text-lg font-bold text-slate-900">Моят профил</h2>
        <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className={labelClass}>Имейл</div>
            <div className="font-medium text-slate-800" data-testid="profile-email">{user?.email}</div>
          </div>
          <div>
            <div className={labelClass}>Роля</div>
            <div className="font-medium text-slate-800">
              {user?.role === "super_admin"
                ? "Главен администратор"
                : user?.role === "admin"
                ? "Администратор"
                : "Редактор"}
            </div>
          </div>
        </div>
      </div>

      {/* Two-factor */}
      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="twofa-section">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="flex items-center gap-2 font-display text-base font-bold text-slate-900">
              <ShieldCheck className="h-5 w-5 text-primary" />
              Двуфакторна автентикация (2FA)
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Допълнителна защита с код от приложение-автентикатор.
            </p>
          </div>
          {isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          ) : (
            <span
              data-testid="twofa-status-badge"
              className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${
                enabled ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"
              }`}
            >
              {enabled ? "Включена" : "Изключена"}
            </span>
          )}
        </div>

        {/* Backup codes display (after enable/regen) */}
        {backupCodes && (
          <div className="mt-5 rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="backup-codes-box">
            <div className="text-sm font-bold text-amber-800">Запазете резервните си кодове</div>
            <p className="mt-1 text-xs text-amber-700">
              Всеки код се използва еднократно. Съхранете ги на сигурно място — няма да ги видите отново.
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2 font-mono text-sm text-slate-800">
              {backupCodes.map((c) => (
                <div key={c} className="rounded bg-white px-2 py-1 text-center tracking-wider">
                  {c}
                </div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={copyCodes} data-testid="copy-backup-codes" className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">
                {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? "Копирано" : "Копирай"}
              </button>
              <button onClick={downloadCodes} className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50">
                <Download className="h-3.5 w-3.5" />
                Изтегли .txt
              </button>
              <button onClick={() => setBackupCodes(null)} data-testid="hide-backup-codes" className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-700">
                Разбрах, скрий
              </button>
            </div>
          </div>
        )}

        {/* Setup flow (QR + code) */}
        {setupData && !enabled && (
          <form onSubmit={confirmEnable} className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="twofa-setup-box">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <Smartphone className="h-4 w-4 text-primary" />
              Сканирайте QR кода с Google Authenticator / Authy
            </div>
            <div className="mt-4 flex flex-col items-center gap-3 sm:flex-row sm:items-start">
              <img src={setupData.qr_data_url} alt="QR код за 2FA" className="h-40 w-40 rounded-lg border border-slate-200 bg-white p-1" data-testid="twofa-qr" />
              <div className="flex-1">
                <div className="text-xs text-slate-500">Или въведете ръчно този ключ:</div>
                <code className="mt-1 block break-all rounded bg-white px-2 py-1.5 font-mono text-xs text-slate-700 ring-1 ring-slate-200" data-testid="twofa-secret">
                  {setupData.secret}
                </code>
                <label className="mt-3 block text-xs font-bold uppercase tracking-wide text-slate-500">
                  Код от приложението
                </label>
                <input
                  className={`${inputClass} mt-1 text-center tracking-[0.3em]`}
                  data-testid="twofa-setup-code"
                  value={setupCode}
                  onChange={(e) => setSetupCode(e.target.value)}
                  placeholder="000000"
                  autoComplete="one-time-code"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setSetupData(null)} className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-800">
                Отказ
              </button>
              <button type="submit" disabled={busy} data-testid="twofa-confirm-enable" className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-white hover:bg-[#0041A8] disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                Включи 2FA
              </button>
            </div>
          </form>
        )}

        {/* Actions */}
        {!setupData && (
          <div className="mt-5">
            {!enabled ? (
              <button onClick={startSetup} disabled={busy} data-testid="twofa-enable-btn" className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-bold text-white hover:bg-[#0041A8] disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                Включи двуфакторна автентикация
              </button>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-600" data-testid="backup-remaining">
                  Оставащи резервни кодове: <span className="font-bold">{status?.backup_codes_remaining ?? 0}</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => setPwPrompt("regen")} data-testid="twofa-regen-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                    <RefreshCw className="h-4 w-4" />
                    Нови резервни кодове
                  </button>
                  <button onClick={() => setPwPrompt("disable")} data-testid="twofa-disable-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50">
                    <ShieldOff className="h-4 w-4" />
                    Изключи 2FA
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Password prompt */}
        {pwPrompt && (
          <form onSubmit={submitPwPrompt} className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="twofa-password-prompt">
            <label className={labelClass}>
              Потвърдете с текущата си парола
            </label>
            <div className="flex gap-2">
              <input type="password" required className={inputClass} data-testid="twofa-prompt-password" value={promptPassword} onChange={(e) => setPromptPassword(e.target.value)} placeholder="••••••••" />
              <button type="submit" disabled={busy} data-testid="twofa-prompt-submit" className="shrink-0 rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Потвърди"}
              </button>
              <button type="button" onClick={() => { setPwPrompt(null); setPromptPassword(""); }} className="shrink-0 rounded-lg px-3 py-2 text-sm font-semibold text-slate-500 hover:text-slate-800">
                Отказ
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Change password */}
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h3 className="flex items-center gap-2 font-display text-base font-bold text-slate-900">
          <Lock className="h-5 w-5 text-primary" />
          Смяна на парола
        </h3>
        <form onSubmit={changePassword} className="mt-4 space-y-4">
          <div>
            <label className={labelClass}>Текуща парола</label>
            <input type="password" required className={inputClass} data-testid="cp-current" value={cpCurrent} onChange={(e) => setCpCurrent(e.target.value)} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Нова парола</label>
              <input type="password" required minLength={6} className={inputClass} data-testid="cp-new" value={cpNew} onChange={(e) => setCpNew(e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Потвърди новата парола</label>
              <input type="password" required minLength={6} className={inputClass} data-testid="cp-confirm" value={cpConfirm} onChange={(e) => setCpConfirm(e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={busy} data-testid="cp-submit" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0041A8] disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
              Смени паролата
            </button>
          </div>
        </form>
      </div>

      {/* Session */}
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h3 className="font-display text-base font-bold text-slate-900">Сесия</h3>
        <p className="mt-1 text-sm text-slate-500">
          Ако подозирате неоторизиран достъп, излезте от всички устройства — това прекратява всички активни сесии, включително тази.
        </p>
        <button onClick={logoutAll} data-testid="logout-all-btn" className="mt-4 inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50">
          <LogOut className="h-4 w-4" />
          Изход от всички устройства
        </button>
      </div>
    </div>
  );
}
